import { useEffect, useRef, useCallback, useState } from 'react';
import { requestNotificationPermission, onForegroundMessage } from '../lib/firebase';
import { toast } from 'sonner';
import api from '../lib/api';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

export function usePushNotifications(isAuthenticated) {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const registered = useRef(false);

  useEffect(() => {
    setPushSupported('Notification' in window && 'serviceWorker' in navigator);
  }, []);

  const enablePush = useCallback(async () => {
    if (!VAPID_KEY) {
      console.warn('[Push] VAPID key not configured');
      return false;
    }

    try {
      // Register service worker first
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      }

      const token = await requestNotificationPermission(VAPID_KEY);
      if (token) {
        // Send token to backend
        await api.post('/notifications/register-device', { fcm_token: token });
        setPushEnabled(true);
        localStorage.setItem('fcm_token', token);
        console.log('[Push] Registered successfully');
        return true;
      }
    } catch (error) {
      console.error('[Push] Registration failed:', error);
    }
    return false;
  }, []);

  const disablePush = useCallback(async () => {
    const token = localStorage.getItem('fcm_token');
    if (token) {
      try {
        await api.post('/notifications/unregister-device', { fcm_token: token });
      } catch {}
      localStorage.removeItem('fcm_token');
    }
    setPushEnabled(false);
  }, []);

  // Auto-register when authenticated and permission already granted
  useEffect(() => {
    if (isAuthenticated && !registered.current && VAPID_KEY) {
      registered.current = true;
      
      if (Notification.permission === 'granted' && localStorage.getItem('fcm_token')) {
        // Re-register existing token
        const existingToken = localStorage.getItem('fcm_token');
        api.post('/notifications/register-device', { fcm_token: existingToken }).catch(() => {});
        setPushEnabled(true);
      }
    }
  }, [isAuthenticated]);

  // Listen for foreground messages
  useEffect(() => {
    if (!pushEnabled) return;

    const unsubscribe = onForegroundMessage(({ title, body, data }) => {
      // Show toast notification for foreground messages
      toast(title, { description: body });
    });

    return unsubscribe;
  }, [pushEnabled]);

  const testPush = useCallback(async () => {
    try {
      const res = await api.post('/notifications/test-push');
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  }, []);

  return {
    pushEnabled,
    pushSupported,
    enablePush,
    disablePush,
    testPush,
  };
}
